import { motion } from "framer-motion";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import {
  TrendingDown,
  Activity,
  BarChart3,
  Shield,
  Package,
  Zap,
  Lock,
  AlertTriangle,
  Eye,
  BookOpen,
  Database,
  FileCheck,
  Mail,
  CheckCircle,
  Building2,
} from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../components/ui/accordion";

interface ForClinicsProps {
  onNavigate?: (page: string) => void;
}

export function ForClinics({ onNavigate }: ForClinicsProps) {
  const benefits = [
    {
      icon: TrendingDown,
      title: "Reduce churn & callbacks",
      description:
        "Spot recurring issues early (e.g., batch quality, packaging, availability) and set realistic patient expectations.",
    },
    {
      icon: Activity,
      title: "Triage smarter",
      description:
        "Understand which products are trending up/down in patient-reported experience before clinic review cycles catch up.",
    },
    {
      icon: BarChart3,
      title: "Benchmark suppliers",
      description:
        "Compare aggregated signals across producers and batches, transparent, and anonymised.",
    },
    {
      icon: Shield,
      title: "Stay compliant",
      description:
        "Patient-led, non-promotional content; sensitive details are gated to verified patients only.",
    },
    {
      icon: Zap,
      title: "Lightweight to adopt",
      description:
        "No EMR surgery. Start with a pilot and weekly insights; deepen integration when ready.",
    },
    {
      icon: Package,
      title: "Innovation without risk",
      description:
        "Our proven verification process reduces security risks and demonstrates a commitment to a transparent CBPM landscape.",
    },
  ];

  const safetyFeatures = [
    {
      icon: Lock,
      title: "Gated sensitive info",
      description: "CBPM information is only available to verified patients.",
    },
    {
      icon: AlertTriangle,
      title: "No medical advice",
      description:
        "Patient experiences only; moderation blocks dosage/clinical claims.",
    },
    {
      icon: Eye,
      title: "Report & remove",
      description: "Clear pathways to flag content that breaches house rules.",
    },
  ];

  const ukDesign = [
    {
      icon: BookOpen,
      title: "Neutral & non-promotional",
      description:
        "Built to respect POM advertising rules and ASA/CAP guidance whilst still being useful.",
    },
    {
      icon: Database,
      title: "Privacy-first",
      description:
        "Explicit UK GDPR (Art. 6 & 9) consent, data minimisation, encryption, and retention policies.",
    },
    {
      icon: FileCheck,
      title: "Clinical reality",
      description:
        "We know products change hands across multiple organisations; our batch view reflects that complexity—sensibly.",
    },
  ];

  const faqs = [
    {
      question: "Is CannaBias a clinic or pharmacy?",
      answer:
        "No. We're a neutral insights platform. We don't prescribe or sell products.",
    },
    {
      question: "Will this add work for our team?",
      answer:
        "Minimal. Start with the weekly insights email and a simple pilot; integrate later if useful.",
    },
    {
      question: "Can we influence reviews?",
      answer:
        "No. Reviews are patient-led and moderated for safety/compliance—not sentiment.",
    },
    {
      question: "Does this replace clinical judgement?",
      answer:
        "Absolutely not. It's ambient signal to support your existing processes.",
    },
    {
      question: "What about data protection?",
      answer:
        "We operate on explicit consent, least-privilege access, and clear retention rules. Full DPIA pack available on request.",
    },
  ];

  return (
    <div className="min-h-screen pt-24 pb-20 px-4 sm:px-6 lg:px-8 gradient-mesh">
      <div className="max-w-4xl mx-auto">
        {/* Hero Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <h1 className="text-4xl sm:text-5xl mb-6">
            Practical patient insight.
            <br />
            Neutral by design.
          </h1>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto mb-8 leading-relaxed">
            CannaBias helps clinics understand how prescribed products perform at
            product and batch level without crossing promotional lines. We don't sell
            products, we don't give medical advice; we surface verified patient feedback
            so you can set expectations, reduce support tickets, and improve outcomes.
          </p>
          <Button
            size="lg"
            className="gradient-green hover:opacity-90 transition-opacity"
            asChild
          >
            <a href="mailto:clinics@cannabias.co.uk">
              <Building2 className="w-5 h-5 mr-2" />
              Register Your Interest
            </a>
          </Button>
        </motion.div>

        {/* Why Partner Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1, duration: 0.6 }}
          className="mb-16"
        >
          <h2 className="text-3xl mb-8 text-center">Why partner with CannaBias</h2>
          <div className="grid md:grid-cols-2 gap-6">
            {benefits.map((benefit, index) => (
              <motion.div
                key={benefit.title}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 + index * 0.05, duration: 0.6 }}
              >
                <Card className="p-6 glass-card h-full hover-lift">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <benefit.icon className="w-6 h-6 text-primary" />
                    </div>
                    <div>
                      <h3 className="text-lg mb-2">{benefit.title}</h3>
                      <p className="text-sm text-muted-foreground leading-relaxed">
                        {benefit.description}
                      </p>
                    </div>
                  </div>
                </Card>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Safety, Anonymity & Security */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2, duration: 0.6 }}
          className="mb-16"
        >
          <h2 className="text-3xl mb-4 text-center">
            Safety, anonymity and security by design
          </h2>
          <p className="text-center text-muted-foreground mb-8 max-w-2xl mx-auto">
            Patient safety & moderation
          </p>
          <div className="grid md:grid-cols-3 gap-6">
            {safetyFeatures.map((feature, index) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 + index * 0.05, duration: 0.6 }}
              >
                <Card className="p-6 glass-card h-full text-center">
                  <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center mx-auto mb-4">
                    <feature.icon className="w-6 h-6 text-primary" />
                  </div>
                  <h3 className="text-lg mb-2">{feature.title}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    {feature.description}
                  </p>
                </Card>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* UK Landscape Design */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3, duration: 0.6 }}
          className="mb-16"
        >
          <h2 className="text-3xl mb-8 text-center">
            Designed for the UK landscape
          </h2>
          <div className="grid md:grid-cols-3 gap-6">
            {ukDesign.map((item, index) => (
              <motion.div
                key={item.title}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 + index * 0.05, duration: 0.6 }}
              >
                <Card className="p-6 glass-card h-full text-center">
                  <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center mx-auto mb-4">
                    <item.icon className="w-6 h-6 text-primary" />
                  </div>
                  <h3 className="text-lg mb-2">{item.title}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    {item.description}
                  </p>
                </Card>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Mission Statement */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4, duration: 0.6 }}
          className="mb-16"
        >
          <Card className="p-8 glass-card border-primary/20 bg-primary/5 text-center">
            <p className="text-xl leading-relaxed">
              We're here to help clinics and patients, not to hype products.{" "}
              <strong className="text-foreground">
                Independent without bias. We are CannaBias.
              </strong>
            </p>
          </Card>
        </motion.div>

        {/* FAQs */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5, duration: 0.6 }}
          className="mb-16"
        >
          <h2 className="text-3xl mb-8 text-center">FAQs for clinics</h2>
          <Card className="p-6 glass-card">
            <Accordion type="single" collapsible className="w-full">
              {faqs.map((faq, index) => (
                <AccordionItem key={index} value={`item-${index}`}>
                  <AccordionTrigger className="text-left">
                    {faq.question}
                  </AccordionTrigger>
                  <AccordionContent className="text-muted-foreground">
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </Card>
        </motion.div>

        {/* CTA Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6, duration: 0.6 }}
        >
          <Card className="p-8 sm:p-12 glass-card border-primary/20 bg-primary/5">
            <div className="text-center">
              <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-6">
                <Mail className="w-8 h-8 text-primary" />
              </div>
              <h2 className="text-3xl mb-4">Next step</h2>
              <p className="text-lg text-muted-foreground mb-8 max-w-2xl mx-auto">
                If you run a clinic and want fewer surprises and better patient
                expectations, let's connect.
              </p>
              <Button
                size="lg"
                className="gradient-green hover:opacity-90 transition-opacity mb-8"
                asChild
              >
                <a href="mailto:clinics@cannabias.co.uk">
                  <Mail className="w-5 h-5 mr-2" />
                  Register Your Interest
                </a>
              </Button>
              <p className="text-sm text-muted-foreground">
                CannaBias is a public, non-transactional platform. No medical advice.
                No sales. UK-first.
              </p>
            </div>
          </Card>
        </motion.div>
      </div>
    </div>
  );
}
